/**
 * Utilities for converting 3D scene objects and related data, mostly for consumption by three.js.
 * @module
 */
import { DRACO_ASSET_SCALE, GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { INTEGER_ID_TRANSPARENT } from "common/constants/pickingConstants";
import {
  assert,
  assertIsExactly,
  assertIsNotInstanceOf,
  assertIsNotNullOrUndefined,
  ensureDefined,
} from "common/utils/assertOfType";
import repeat from "common/utils/repeat";
import { aabb2AddPoint, aabb2New } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3DiagLength } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { BRep, Extrusion, Face } from "geom/brep";
import type { PlaneBSP } from "geom/bspTypes";
import type { Mat3 } from "geom/mat3";
import type { Mat4 } from "geom/mat4";
import type { Mesh3 } from "geom/mesh";
import { mesh3Copy, mesh3New } from "geom/mesh";
import type { Plane } from "geom/plane";
import { planeNew, planeNormal } from "geom/plane";
import { polyLine2IsClosed } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3AABB3, polyLine3ByTwoPoints } from "geom/polyLine3";
import { tessBRep, tessBreps, tessExtrusion, tessExtrusions, tessFace3, tessFaces3 } from "geom/tess";
import { vec3AddScale, vec3TriangleNormal } from "geom/vec3";
import type { Box3, LineDashedMaterial, Material, Object3D } from "three";
import * as THREE from "three";
import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  GLBufferAttribute,
  InterleavedBufferAttribute,
  Line,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  PointLight,
  Points,
  PointsMaterial,
  Vector3,
} from "three";
// Re-export for backwards compatibility
import { allocateMultiPolyLine2ToTHREEStrokeBuffers } from "./allocateBuffers";
import type { BufferGeometryAllocator } from "./BufferGeometryAllocator";
import type { CameraSettings } from "./CameraSettings";
import { defaultCameraSettings, orthoCameraBounds } from "./CameraSettings";
import { convertIdArrayToVec4Array, decodeVec4ToId } from "./gpuPicking";
import { SHADOW_GEOMETRY_LAYER } from "./layerConstants";
import { LineBasicMaterialFactory } from "./materials/LineBasicMaterialFactory";
import type { LineStrokeMaterial } from "./materials/lineStrokeMaterial";
import { setMeshIdAttribute, setSingleIdAttribute } from "./meshIdAttribute";
import type { StrokeStyle } from "./stroke";
import { CapType, DefaultStrokeStyle, JoinType, StrokedGeom, strokePolyline2Mutate } from "./stroke";
import type THREEAssetLoader from "./THREEAssetLoader";
import type { THREECamera } from "./THREECamera";

export const xyPlane = { ...planeNew(), uX: 1, vY: 1 };
export const xzPlane = { ...planeNew(), uX: 1, vZ: 1 };
export const yzPlane = { ...planeNew(), uY: 1, vZ: 1 };

export function makeLine(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  mat: LineBasicMaterial,
) {
  const x = new BufferGeometry();

  const vertices = new Float32Array([x0, y0, z0, x1, y1, z1]);

  x.setAttribute("position", new BufferAttribute(vertices, 3));

  return new Line(x, mat);
}

export const BASE_MESH_MAT_PROPS = {
  opacity: 1,
  transparent: false,
  color: 0xeeeeee,
  roughness: 1.0,
};

let defaultMeshmat;
function getDefaultMeshMaterial() {
  if (defaultMeshmat) {
    return defaultMeshmat;
  }
  return (defaultMeshmat = new MeshStandardMaterial({
    ...BASE_MESH_MAT_PROPS,
    wireframe: false,
    side: DoubleSide,
  }));
}

let defaultLineMat;
function getDefaultLineMaterial() {
  if (defaultLineMat) {
    return defaultLineMat;
  }
  return (defaultLineMat = LineBasicMaterialFactory.instance.getMaterial({ color: 0x000000 }));
}

let defaultPointMat;
function getDefaultPointsMaterial() {
  if (defaultPointMat) {
    return defaultPointMat;
  }
  return (defaultPointMat = new PointsMaterial({ size: 5, color: 0x000000, sizeAttenuation: false }));
}

export function line3ToTHREE(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  mat: number | LineBasicMaterial = getDefaultLineMaterial(),
) {
  if (typeof mat === "number") {
    mat = LineBasicMaterialFactory.instance.getMaterial({ color: mat });
  }

  return makeLine(x0, y0, z0, x1, y1, z1, mat);
}

function createPolyLine2Geometry(path: PolyLine2, plane: Plane, id: number | undefined): BufferGeometry {
  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(polyLine3OnPlane32(path, plane), 3));
  if (typeof id === "number") {
    g.setAttribute("id", new BufferAttribute(convertIdArrayToVec4Array(repeat(path.length / 2, id)), 4));
  }
  return g;
}

export function polyLine2ToTHREE({
  path,
  plane = xyPlane,
  material = getDefaultLineMaterial(),
  id = undefined,
  renderOrder = 0,
}: {
  path: PolyLine2;
  plane?: Plane;
  material?: number | LineBasicMaterial;
  id?: number | undefined;
  renderOrder?: number;
}) {
  if (typeof material === "number") {
    material = LineBasicMaterialFactory.instance.getMaterial({ color: material });
  }

  const g = createPolyLine2Geometry(path, plane, id);
  const line = new Line(g, material);
  line.renderOrder = renderOrder;
  return line;
}

export function multiPolyLine2ToTHREE({
  paths,
  plane = xyPlane,
  material = getDefaultLineMaterial(),
  id = undefined,
  renderOrder = 0,
}: {
  paths: PolyLine2[];
  plane?: Plane;
  material?: number | LineBasicMaterial;
  id?: number | number[] | undefined;
  renderOrder?: number;
}) {
  if (typeof material === "number") {
    material = LineBasicMaterialFactory.instance.getMaterial({ color: material });
  }

  if (Array.isArray(id)) {
    assert(id.length === paths.length, "Id count must exactly equal path count");
  }

  const points: number[] = [];
  const indices: number[] = [];
  const ids: number[] = [];

  let vertexOffset = 0;

  for (let pathIndex = 0; pathIndex < paths.length; pathIndex++) {
    const path = paths[pathIndex];
    const pointCount = path.length / 2;
    points.push(...path);

    for (let pointIndex = 0; pointIndex < pointCount - 1; pointIndex++) {
      indices.push(vertexOffset + pointIndex, vertexOffset + pointIndex + 1);
    }

    const pathId = Array.isArray(id) ? id[pathIndex] : id;
    if (typeof pathId === "number") {
      ids.push(...repeat(pointCount, pathId));
    }

    vertexOffset += pointCount;
  }

  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(polyLine3OnPlane32(points, plane), 3));
  g.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

  if (id !== undefined) {
    g.setAttribute("id", new BufferAttribute(convertIdArrayToVec4Array(ids), 4));
  }

  const lineSegments = new LineSegments(g, material);
  lineSegments.renderOrder = renderOrder;
  return lineSegments;
}

const strokedGeom = new StrokedGeom();

export function polyLine2ToTHREEStroke({
  path,
  plane = xyPlane,
  material,
  strokeStyle = DefaultStrokeStyle,
  id = undefined,
  renderOrder = 0,
  allocator,
}: {
  path: PolyLine2;
  plane?: Plane;
  material: LineStrokeMaterial;
  strokeStyle?: StrokeStyle;
  id?: number | undefined;
  renderOrder?: number;
  allocator?: BufferGeometryAllocator;
}): THREE.Mesh {
  return multiPolyLine2ToTHREEStroke({
    paths: [path],
    plane,
    material,
    strokeStyle,
    id,
    renderOrder,
    allocator,
  });
}

export function multiPolyLine2ToTHREEStroke({
  paths,
  plane = xyPlane,
  material,
  strokeStyle = DefaultStrokeStyle,
  id = undefined,
  renderOrder = 0,
  allocator,
}: {
  paths: PolyLine2[];
  plane?: Plane;
  material: LineStrokeMaterial;
  strokeStyle?: StrokeStyle;
  id?: number | number[] | undefined;
  renderOrder?: number;
  allocator?: BufferGeometryAllocator;
}): THREE.Mesh {
  /**
   * Concatenate all of the paths into a single array set
   */
  const pts: number[] = [];
  const norms: number[] = [];
  const tex: number[] = [];
  const dst: number[] = [];
  const tris: number[] = [];
  const ids: number[] = [];

  if (Array.isArray(id)) {
    assert(id.length === paths.length, "Id count must exactly equal path count");
  }

  for (let j = 0; j < paths.length; j++) {
    const ptOffset = pts.length / 2;
    const path = paths[j];

    strokedGeom.clear();
    strokePolyline2Mutate(strokedGeom, path, strokeStyle, 0, polyLine2IsClosed(path));

    for (let i = 0, l = strokedGeom.pts.length; i < l; i++) {
      pts.push(strokedGeom.pts[i]);
    }

    for (let i = 0, l = strokedGeom.norms.length; i < l; i++) {
      norms.push(strokedGeom.norms[i]);
    }

    for (let i = 0, l = strokedGeom.tex.length; i < l; i++) {
      tex.push(strokedGeom.tex[i]);
    }

    for (let i = 0, l = strokedGeom.dst.length; i < l; i++) {
      dst.push(strokedGeom.dst[i]);
    }

    for (let i = 0, l = strokedGeom.tris.length; i < l; i++) {
      tris.push(strokedGeom.tris[i] + ptOffset);
    }

    const pathId = Array.isArray(id) ? id[j] : id;

    if (typeof pathId === "number") {
      ids.push(...repeat(strokedGeom.pts.length / 2, pathId));
    }
  }

  let geom: BufferGeometry;

  if (!allocator) {
    geom = new BufferGeometry();

    geom.setAttribute("position", new BufferAttribute(polyLine3OnPlane32(pts, plane), 3));
    geom.setAttribute("normal", new BufferAttribute(projectNormalsToPlane(norms, plane), 3));
    geom.setAttribute("texOffset", new BufferAttribute(new Float32Array(tex), 1));
    geom.setAttribute("distance", new BufferAttribute(new Float32Array(dst), 1));
    geom.setIndex(new BufferAttribute(new Uint32Array(tris), 1));

    if (id !== undefined) {
      geom.setAttribute("id", new BufferAttribute(convertIdArrayToVec4Array(ids), 4));
    }
  } else {
    const verts = polyLine3OnPlane32(pts, plane);
    geom = allocator.alloc(verts.length, tris.length);

    setAttribute(geom, "position", verts);
    setAttribute(geom, "normal", projectNormalsToPlane(norms, plane));
    setAttribute(geom, "texOffset", tex);
    setAttribute(geom, "distance", dst);

    setIndex(geom, tris);

    if (id !== undefined) {
      setAttribute(geom, "id", convertIdArrayToVec4Array(ids));
    } else {
      setAttribute(geom, "id", convertIdArrayToVec4Array(repeat(pts.length / 2, INTEGER_ID_TRANSPARENT)));
    }

    geom.setDrawRange(0, tris.length);

    /**
     * When using a BufferGeometryAllocator, we need to manually compute the bounding sphere
     * to ensure three.js's view frustum culling works correctly. Otherwise, the bounding sphere
     * will be determined from the previous geometry in the allocator.
     */
    geom.computeBoundingSphere();

    geom.userData.isCached = true;
  }

  const mesh = new Mesh(geom, material);
  mesh.renderOrder = renderOrder;

  return mesh;
}

function setAttribute(g: BufferGeometry, name: string, buffer: ArrayLike<number>) {
  const attr = ensureDefined(g.getAttribute(name));
  attr.array.set(buffer);
  attr.needsUpdate = true;
}

function setIndex(g: BufferGeometry, buffer: ArrayLike<number>) {
  const index = ensureDefined(g.getIndex());

  index.array.set(buffer);
  index.needsUpdate = true;
}

/**
 * Pre-allocates a Mesh to hold stroke geometry that will be allocated later.
 */
export function allocateMultiPolyLine2ToTHREEStrokeMesh(
  material: THREE.Material,
  maxVertexCount: number,
  maxTriCount: number,
): THREE.Mesh {
  return new Mesh(allocateMultiPolyLine2ToTHREEStrokeBuffers(maxVertexCount, maxTriCount), material);
}

export { allocateMultiPolyLine2ToTHREEStrokeBuffers } from "./allocateBuffers";

export function polyLine3OnPlane32(
  pl: ArrayLike<number>,
  { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ }: Plane,
): Float32Array {
  const res = new Float32Array((pl.length / 2) * 3);

  for (let i = 0, l = pl.length / 2; i < l; i++) {
    const i2 = 2 * i;
    const i3 = 3 * i;

    const u = pl[i2];
    const v = pl[i2 + 1];

    res[i3] = originX + uX * u + vX * v;
    res[i3 + 1] = originY + uY * u + vY * v;
    res[i3 + 2] = originZ + uZ * u + vZ * v;
  }

  return res;
}

function projectNormalsToPlane(norms: ArrayLike<number>, { uX, uY, uZ, vX, vY, vZ }: Plane): Float32Array {
  const res = new Float32Array((norms.length / 2) * 3);

  for (let i = 0, l = norms.length / 2; i < l; i++) {
    const i2 = 2 * i;
    const i3 = 3 * i;

    const u = norms[i2];
    const v = norms[i2 + 1];

    res[i3] = uX * u + vX * v;
    res[i3 + 1] = uY * u + vY * v;
    res[i3 + 2] = uZ * u + vZ * v;
  }

  return res;
}

export function polyLine2ToTHREEDashed({
  path,
  plane = xyPlane,
  renderOrder = 0,
  material,
  id = undefined,
}: {
  path: PolyLine2;
  plane?: Plane;
  renderOrder?: number;
  material: LineDashedMaterial;
  id?: number;
}) {
  const g = createPolyLine2Geometry(path, plane, id);
  const line = new Line(g, material);
  line.renderOrder = renderOrder;
  return line;
}

export const WitnessLineStyle: StrokeStyle = {
  capTypeStart: CapType.Round,
  startArrowLength: 0,
  startArrowWidth: 0,

  capTypeEnd: CapType.Round,
  endArrowLength: 0,
  endArrowWidth: 0,

  joinType: JoinType.Miter,
  miterLimit: 10,
  roundAngleIncrement: 0.05,

  allowDashed: false,
};

export function polyLine3ToTHREELineSegments(
  pl: PolyLine3,
  mat: number | LineBasicMaterial = getDefaultLineMaterial(),
  renderOrder: number = 0,
) {
  if (typeof mat === "number") {
    mat = LineBasicMaterialFactory.instance.getMaterial({ color: mat });
  }

  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(new Float32Array(pl), 3));
  const lineSegments = new LineSegments(g, mat);
  lineSegments.renderOrder = renderOrder;
  return lineSegments;
}

export function polyLine3ToTHREE(
  pl: PolyLine3,
  lineMat: number | LineStrokeMaterial | LineBasicMaterial = getDefaultLineMaterial(),
  renderOrder: number = 0,
) {
  if (typeof lineMat === "number") {
    lineMat = LineBasicMaterialFactory.instance.getMaterial({ color: lineMat });
  }

  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(new Float32Array(pl), 3));
  const line = new Line(g, lineMat);
  line.renderOrder = renderOrder;
  return line;
}

export function polylines3ToTHREELineSegments(pls: PolyLine3[], mat: THREE.LineBasicMaterial) {
  const verts: number[] = [];
  const indices: number[] = [];
  let idx = 0;
  for (const pl3 of pls) {
    verts.push(...pl3);
    for (let v = 0; v < pl3.length / 3 - 1; v++) {
      indices.push(idx + v);
      indices.push(idx + v + 1);
    }
    idx += pl3.length / 3;
  }

  const position = new THREE.Float32BufferAttribute(verts, 3);
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", position);
  geom.setIndex(indices);

  const lineSegments = new THREE.LineSegments(geom, mat);
  return lineSegments;
}

export function vec3sToTHREE(
  pl: Float32Array,
  mat: number | Material = getDefaultPointsMaterial(),
  renderOrder: number = 0,
) {
  if (!(pl instanceof Float32Array)) {
    throw new Error("Points must be a Float32Array");
  }

  if (typeof mat === "number") {
    mat = new PointsMaterial({ size: 8, color: mat, sizeAttenuation: false });
  }

  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(pl, 3));

  const points = new Points(g, mat);
  points.renderOrder = renderOrder;
  return points;
}

export function vec3ToTHREE(
  x: number,
  y: number,
  z: number = 0,
  mat: number | PointsMaterial = getDefaultPointsMaterial(),
  renderOrder: number = 0,
) {
  return vec3sToTHREE(new Float32Array([x, y, z]), mat, renderOrder);
}

export function extrusionToTHREE(ext: Extrusion, mat: Material = getDefaultMeshMaterial(), id?: number) {
  return mesh3ToTHREE(tessExtrusion(ext, id), mat);
}

export function brepToTHREE(brep: BRep, mat: Material = getDefaultMeshMaterial(), id?: number) {
  return mesh3ToTHREE(tessBRep(brep, id), mat);
}

export function faceToTHREE(
  face: Face,
  mat: Material = getDefaultMeshMaterial(),
  id?: number,
  renderOrder: number = 0,
) {
  const mesh = mesh3ToTHREE(tessFace3(face, id), mat);
  mesh.renderOrder = renderOrder;
  return mesh;
}

export function extrusionsToTHREE(exts: Extrusion[], mat: Material = getDefaultMeshMaterial(), id?: number | number[]) {
  return mesh3ToTHREE(tessExtrusions(exts, id), mat);
}

export function brepsToTHREE(breps: BRep[], mat: Material = getDefaultMeshMaterial(), id?: number | number[]) {
  return mesh3ToTHREE(tessBreps(breps, id), mat);
}

export function facesToTHREE(
  faces: Face[],
  mat: Material = getDefaultMeshMaterial(),
  id?: number | number[],
  renderOrder: number = 0,
) {
  const mesh = mesh3ToTHREE(tessFaces3(faces, id), mat);
  mesh.renderOrder = renderOrder;
  return mesh;
}

export function pointLightToTHREE(x: number, y: number, z: number) {
  const light = new PointLight(0xffffff, 0.15);
  light.position.set(x, y, z);
  light.updateMatrix();
  light.updateMatrixWorld();
  return light;
}

export function mesh3ToTHREEGeometry(mesh: Mesh3): THREE.BufferGeometry {
  const g = new BufferGeometry();

  console.assert(mesh.verts instanceof Float32Array);
  console.assert(mesh.norms === undefined || mesh.norms instanceof Float32Array);
  console.assert(mesh.uvs1 === undefined || mesh.uvs1 instanceof Float32Array);
  g.setAttribute("position", new BufferAttribute(mesh.verts, 3));
  if (mesh.norms) {
    g.setAttribute("normal", new BufferAttribute(mesh.norms, 3));
  }
  if (mesh.uvs1) {
    g.setAttribute("uv", new BufferAttribute(mesh.uvs1, 2));
  }
  if (mesh.ids) {
    if (mesh.ids.length === 1) {
      setSingleIdAttribute(g, mesh.ids[0]);
    } else {
      g.setAttribute("id", new BufferAttribute(convertIdArrayToVec4Array(mesh.ids), 4));
    }
  }
  g.setIndex(new BufferAttribute(mesh.tris, 1));

  return g;
}

export function mesh3ToTHREE(mesh: Mesh3, mat: number | Material = getDefaultMeshMaterial()) {
  const g = mesh3ToTHREEGeometry(mesh);

  if (typeof mat === "number") {
    mat = new MeshStandardMaterial({ color: mat });
  }

  const tmesh = new THREE.InstancedMesh(g, mat, 1);
  if (mat.opacity === 1) {
    tmesh.castShadow = true;
    tmesh.receiveShadow = true;
  }

  tmesh.matrixAutoUpdate = false;

  return tmesh;
}

export function transformMesh3(mesh: Mesh3, transform: Mat4): Mesh3 {
  const geometry = mesh3ToTHREEGeometry(mesh3Copy(mesh));
  geometry.applyMatrix4(mat4ToTHREE(transform));
  return threeBufferGeometryToMesh3(geometry);
}

function deinterleaveBufferAttribute(attribute: InterleavedBufferAttribute): Float32Array {
  const { count, itemSize } = attribute;
  const array = new Float32Array(count * itemSize);
  if (itemSize === 3) {
    const tmpVector = new THREE.Vector3();
    for (let i = 0; i < count; i++) {
      tmpVector.fromBufferAttribute(attribute, i);
      array[i * 3] = tmpVector.x;
      array[i * 3 + 1] = tmpVector.y;
      array[i * 3 + 2] = tmpVector.z;
    }
  } else if (itemSize === 2) {
    const tmpVector = new THREE.Vector2();
    for (let i = 0; i < count; i++) {
      tmpVector.fromBufferAttribute(attribute as any as BufferAttribute, i); // NOTE: Type definition in three.js types is wrong, InterleavedBufferAttribute is a valid input here.
      array[i * 2] = tmpVector.x;
      array[i * 2 + 1] = tmpVector.y;
    }
  }
  return array;
}

interface IncludeAttributes {
  normal: boolean;
  uv: boolean;
  uv1: boolean;
  id: boolean;
}

export function threeBufferGeometryToMesh3(
  g: BufferGeometry,
  includeAttributes: IncludeAttributes = { normal: true, uv: true, uv1: true, id: true },
): Mesh3 {
  let idArray: Uint32Array | undefined;
  if (includeAttributes.id && g.hasAttribute("id")) {
    const idAttr: BufferAttribute = g.getAttribute("id") as BufferAttribute;
    console.assert(idAttr.isBufferAttribute);
    if (idAttr) {
      idArray = new Uint32Array(idAttr.array.length / 4);
      for (let i = 0, l = idAttr.array.length / 4; i < l; ++i) {
        const decoded = decodeVec4ToId(idAttr.array as Float32Array, i * 4);
        idArray[i] = decoded;
      }
    }
  }
  const positionAttribute: BufferAttribute = g.getAttribute("position") as BufferAttribute;
  console.assert(positionAttribute.isBufferAttribute);
  assertIsNotInstanceOf(
    positionAttribute,
    GLBufferAttribute,
    "Cannot convert three.js mesh using GLBufferAttribute to Mesh3",
  );
  let positionArray = positionAttribute.array as Float32Array;
  if (positionAttribute instanceof InterleavedBufferAttribute) {
    positionArray = deinterleaveBufferAttribute(positionAttribute);
  }

  const normalAttribute: BufferAttribute | undefined = g.getAttribute("normal") as BufferAttribute | undefined;
  console.assert(normalAttribute === undefined || normalAttribute.isBufferAttribute);
  assertIsNotInstanceOf(
    normalAttribute,
    GLBufferAttribute,
    "Cannot convert three.js mesh using GLBufferAttribute to Mesh3",
  );
  let normalArray = includeAttributes.normal ? (normalAttribute?.array as Float32Array) : undefined;
  if (includeAttributes.normal && normalAttribute instanceof InterleavedBufferAttribute) {
    normalArray = deinterleaveBufferAttribute(normalAttribute);
  }

  const uvAttribute: BufferAttribute | undefined = g.getAttribute("uv") as BufferAttribute | undefined;
  console.assert(uvAttribute === undefined || uvAttribute.isBufferAttribute);
  assertIsNotInstanceOf(
    uvAttribute,
    GLBufferAttribute,
    "Cannot convert three.js mesh using GLBufferAttribute to Mesh3",
  );
  let uvArray = includeAttributes.uv ? (uvAttribute?.array as Float32Array) : undefined;
  if (includeAttributes.uv && uvAttribute instanceof InterleavedBufferAttribute) {
    uvArray = deinterleaveBufferAttribute(uvAttribute);
  }

  const uv1Attribute: BufferAttribute | undefined = g.getAttribute("uv1") as BufferAttribute | undefined;
  console.assert(uv1Attribute === undefined || uv1Attribute.isBufferAttribute);
  assertIsNotInstanceOf(
    uv1Attribute,
    GLBufferAttribute,
    "Cannot convert three.js mesh using GLBufferAttribute to Mesh3",
  );
  let uv1Array = includeAttributes.uv1 ? (uv1Attribute?.array as Float32Array) : undefined;
  if (includeAttributes.uv1 && uv1Attribute instanceof InterleavedBufferAttribute) {
    uv1Array = deinterleaveBufferAttribute(uv1Attribute);
  }

  return mesh3New(positionArray, g.getIndex()?.array as Uint32Array, normalArray, uvArray, uv1Array, idArray);
}

export function aabb3ToTHREE(aabb3: AABB3, mat: Material = getDefaultMeshMaterial()) {
  const sizeX = aabb3.maxX - aabb3.minX;
  const sizeY = aabb3.maxY - aabb3.minY;
  const sizeZ = aabb3.maxZ - aabb3.minZ;

  const geometry = new BoxGeometry(sizeX, sizeY, sizeZ);

  const mesh = new Mesh(geometry, mat);
  mesh.position.set((aabb3.minX + aabb3.maxX) * 0.5, (aabb3.minY + aabb3.maxY) * 0.5, (aabb3.minZ + aabb3.maxZ) * 0.5);
  return mesh;
}

export async function dracoToTHREE(
  assetLoader: THREEAssetLoader,
  dracoURL: string,
  mat: Material | undefined,
  scale: number = DRACO_ASSET_SCALE,
  scaleUV: boolean = false,
  scaleUVMult: number = 1.0,
): Promise<Mesh> {
  const geo = await assetLoader.loadDRACO(dracoURL);

  if (scaleUV && geo.boundingBox) {
    const appliedUVScale = computeUVScale(geo.boundingBox, scale, scaleUVMult);
    if (geo.userData && geo.userData.appliedUVScale !== undefined) {
      // Geometry objects might be fetched from cache, often when performing a hot reload. Prevent applying UV scale multiple times in this case.
      scaleBufferGeometryUVs(geo, appliedUVScale / geo.userData.appliedUVScale);
    } else {
      scaleBufferGeometryUVs(geo, appliedUVScale);
    }
    if (!geo.userData) {
      geo.userData = {
        appliedUVScale,
      };
    } else {
      geo.userData.appliedUVScale = appliedUVScale;
    }
  }

  const mesh = new THREE.InstancedMesh(geo, mat, 1);
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  mesh.scale.set(scale, scale, scale);
  mesh.updateMatrix();
  mesh.updateMatrixWorld();
  mesh.matrixAutoUpdate = false;

  if (!geo.userData) {
    geo.userData = {
      isCached: true,
    };
  } else {
    geo.userData.isCached = true;
  }

  return mesh;
}

export function applyIdToProp(prop: Object3D, id: number) {
  prop.traverse((node) => {
    if (node instanceof Mesh) {
      setMeshIdAttribute(node, id);
    }
  });
}

/**
 * Flattens a three.js hierarchy into an array of meshes with ancestor transforms baked in.
 * If the input node has a parent, the returned meshes are reparented under that former parent.
 */
export function flattenTHREEHierarchy(node: THREE.Object3D): THREE.Mesh[] {
  const formerParent = node.parent;
  node.updateWorldMatrix(true, true);
  const formerParentMatrixWorldInverse = formerParent ? formerParent.matrixWorld.clone().invert() : undefined;
  const meshes: THREE.Mesh[] = [];
  node.traverse((childNode) => {
    if (childNode instanceof THREE.Mesh) {
      if (formerParentMatrixWorldInverse) {
        childNode.matrix.multiplyMatrices(formerParentMatrixWorldInverse, childNode.matrixWorld);
      } else {
        childNode.matrix.copy(childNode.matrixWorld);
      }
      childNode.matrix.decompose(childNode.position, childNode.quaternion, childNode.scale);
      meshes.push(childNode);
    }
  });
  for (const mesh of meshes) {
    mesh.removeFromParent();
    if (formerParent) {
      formerParent.add(mesh);
    }
    mesh.updateMatrixWorld(true);
  }
  return meshes;
}

export async function gltfToTHREE(
  assetLoader: THREEAssetLoader,
  gltfURL: string,
  scale: number = GLTF_ASSET_SCALE,
  prefix?: string | null,
): Promise<THREE.Object3D> {
  let prop = (await assetLoader.loadGLTF(gltfURL, prefix)).clone();
  const meshes: THREE.Mesh[] = [];
  prop.traverse((node) => {
    if (node instanceof Mesh) {
      meshes.push(node);
    }
  });

  for (let mesh of meshes) {
    const oldMesh = mesh;
    mesh = new THREE.InstancedMesh(mesh.geometry, mesh.material, 1);

    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (!mesh.geometry.userData) {
      mesh.geometry.userData = {
        isCached: true,
      };
    } else {
      mesh.geometry.userData.isCached = true;
    }

    // Having UV1, UV2, UV3 attributes will cause three.js to generate a separate shader variant. Eliminate them. UV0 is all we need.
    if (mesh.geometry.hasAttribute("uv1")) {
      mesh.geometry.deleteAttribute("uv1");
    }
    if (mesh.geometry.hasAttribute("uv2")) {
      mesh.geometry.deleteAttribute("uv2");
    }
    if (mesh.geometry.hasAttribute("uv3")) {
      mesh.geometry.deleteAttribute("uv3");
    }

    // Copy relevant properties to the new instanced mesh.
    mesh.name = oldMesh.name;
    mesh.userData = oldMesh.userData;
    mesh.position.copy(oldMesh.position);
    mesh.rotation.copy(oldMesh.rotation);
    mesh.quaternion.copy(oldMesh.quaternion);
    mesh.scale.copy(oldMesh.scale);
    mesh.modelViewMatrix.copy(oldMesh.modelViewMatrix);
    mesh.normalMatrix.copy(oldMesh.normalMatrix);
    mesh.matrix.copy(oldMesh.matrix);
    mesh.matrixWorld.copy(oldMesh.matrixWorld);

    if (prop === oldMesh) {
      prop = mesh;
    } else {
      assertIsNotNullOrUndefined(oldMesh.parent);
      assertIsExactly(oldMesh.children.length, 0);
      oldMesh.parent.add(mesh);
      oldMesh.removeFromParent();
    }
  }

  prop.scale.set(scale, scale, scale);
  // https://threejs.org/docs/#manual/en/introduction/Matrix-transformations
  prop.updateMatrix();
  prop.updateMatrixWorld();
  prop.matrixAutoUpdate = false;
  return prop;
}

export function debugPlaneToTHREE(plane: Plane, scale: number = 20) {
  const [normX, normY, normZ] = planeNormal(plane);

  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = plane;

  const [endNX, endNY, endNZ] = vec3AddScale(originX, originY, originZ, normX, normY, normZ, scale);
  const [endUX, endUY, endUZ] = vec3AddScale(originX, originY, originZ, uX, uY, uZ, scale);
  const [endVX, endVY, endVZ] = vec3AddScale(originX, originY, originZ, vX, vY, vZ, scale);

  const [subUX, subUY, subUZ] = vec3AddScale(originX, originY, originZ, uX, uY, uZ, -scale);
  const [subVX, subVY, subVZ] = vec3AddScale(originX, originY, originZ, vX, vY, vZ, -scale);

  const group = new Group();

  const LINE_RED_MAT = new LineBasicMaterial({ color: 0xff0000, name: "convert LINE_RED_MAT" });
  const LINE_GREEN_MAT = new LineBasicMaterial({ color: 0x00ff00, name: "convert LINE_GREEN_MAT" });
  const LINE_BLUE_MAT = new LineBasicMaterial({ color: 0x0000ff, name: "convert LINE_BLUE_MAT" });
  const LINE_GREY_MAT = new LineBasicMaterial({ color: 0xaaaaaa, name: "convert LINE_GREY_MAT" });

  group.add(makeLine(originX, originY, originZ, endUX, endUY, endUZ, LINE_RED_MAT));
  group.add(makeLine(originX, originY, originZ, endVX, endVY, endVZ, LINE_GREEN_MAT));
  group.add(makeLine(originX, originY, originZ, endNX, endNY, endNZ, LINE_BLUE_MAT));

  group.add(makeLine(originX, originY, originZ, subUX, subUY, subUZ, LINE_GREY_MAT));
  group.add(makeLine(originX, originY, originZ, subVX, subVY, subVZ, LINE_GREY_MAT));

  return group;
}

export function planeBSPToTHREEClippingPlane(planeBSP: PlaneBSP, planeOut?: THREE.Plane): THREE.Plane {
  planeOut = planeOut ?? new THREE.Plane();

  // In three.js, points that are behind the plane (dot product with the normal is negative) are cut away.
  // In BSP clipping, we use the opposite convention.
  planeOut.normal.set(-planeBSP.nX, -planeBSP.nY, -planeBSP.nZ);

  // Normals being equal, Three.js plane constant is the opposite of the PlaneBSP dist. Since we negate the normal, we don't negate dist.
  planeOut.constant = planeBSP.dist;

  return planeOut;
}

/**
 * Convert a plane to a matrix by using the plane U, V and normal vectors as basis vectors.
 * @param plane
 * @param matrixOut Optional output matrix to avoid allocation.
 */
export function planeToTHREEMatrix4(plane: Plane, matrixOut?: THREE.Matrix4): THREE.Matrix4 {
  matrixOut = matrixOut ?? new THREE.Matrix4();

  const normal = planeNormal(plane);
  matrixOut.set(
    plane.uX,
    plane.vX,
    normal[0],
    plane.originX,
    plane.uY,
    plane.vY,
    normal[1],
    plane.originY,
    plane.uZ,
    plane.vZ,
    normal[2],
    plane.originZ,
    0,
    0,
    0,
    1,
  );

  return matrixOut;
}

export function cameraToTHREE(cameraSettings: CameraSettings, aspect: number): THREECamera {
  let threeCamera: THREECamera;

  const {
    name,
    fov,
    x,
    y,
    z,
    ortho,
    targetX,
    targetY,
    targetZ,
    minOrthoWidth,
    minOrthoHeight,
    upX,
    upY,
    upZ,
    cameraNear = defaultCameraSettings.cameraNear,
    cameraFar = defaultCameraSettings.cameraFar,
  } = cameraSettings;

  // The ratio between cameraNear and cameraFar is related to the depth
  // buffer precision by log2(cameraFar/cameraNear). So, avoid setting
  // cameraNear close or you lose precision fast.

  // https://www.khronos.org/opengl/wiki/Depth_Buffer_Precision

  if (!ortho) {
    threeCamera = new PerspectiveCamera(fov, aspect, cameraNear, cameraFar);
  } else {
    const { left, right, top, bottom } = orthoCameraBounds(aspect, minOrthoWidth, minOrthoHeight);
    threeCamera = new OrthographicCamera(left, right, top, bottom, cameraNear, cameraFar);
  }

  threeCamera.up.set(upX, upY, upZ);
  threeCamera.position.set(x, y, z);
  threeCamera.lookAt(targetX, targetY, targetZ);

  threeCamera.updateMatrixWorld();
  threeCamera.updateProjectionMatrix();

  threeCamera.layers.enableAll();
  threeCamera.layers.disable(SHADOW_GEOMETRY_LAYER);

  threeCamera.name = name;
  return threeCamera;
}

export function topDownAABBToRelativeViewportAABB(
  { minX, minY, maxX, maxY }: AABB2,
  camera: THREECamera,
  z: number = 0,
): AABB2 {
  camera.updateMatrixWorld();
  const worldVec = new Vector3(minX, minY, z);
  worldVec.project(camera);
  const screenAABB = aabb2New(worldVec.x, worldVec.y, worldVec.x, worldVec.y);
  worldVec.set(maxX, minY, z);
  worldVec.project(camera);
  aabb2AddPoint(screenAABB, worldVec.x, worldVec.y);
  worldVec.set(minX, maxY, z);
  worldVec.project(camera);
  aabb2AddPoint(screenAABB, worldVec.x, worldVec.y);
  worldVec.set(maxX, maxY, z);
  worldVec.project(camera);
  aabb2AddPoint(screenAABB, worldVec.x, worldVec.y);

  // Convert from clip space (from -1 to 1) to normalized space (0 to 1).
  screenAABB.minX = screenAABB.minX * 0.5 + 0.5;
  screenAABB.minY = screenAABB.minY * 0.5 + 0.5;
  screenAABB.maxX = screenAABB.maxX * 0.5 + 0.5;
  screenAABB.maxY = screenAABB.maxY * 0.5 + 0.5;

  return screenAABB;
}

function computeUVScale(box: Box3, scale: number, scaleUVMult: number) {
  const modelSize = new Vector3();
  box.getSize(modelSize);
  const maxDim = Math.max(modelSize.x, Math.max(modelSize.y, modelSize.z));
  return maxDim * scale * scaleUVMult;
}

export function scaleBufferGeometryUVs(geometry: BufferGeometry, scaleFactor: number) {
  const uvAttribute: BufferAttribute = geometry.getAttribute("uv") as BufferAttribute;
  console.assert(uvAttribute.isBufferAttribute);
  const uvs = uvAttribute.array as Float32Array;
  for (let i = 0, ic = uvs.length; i < ic; i++) {
    uvs[i] = uvs[i] * scaleFactor;
  }
}

export function translateBufferGeometryUVs(geometry: BufferGeometry, offsetX: number, offsetY: number) {
  const uvAttribute: BufferAttribute = geometry.getAttribute("uv") as BufferAttribute;
  console.assert(uvAttribute.isBufferAttribute);
  const uvs = uvAttribute.array as Float32Array;
  for (let i = 0, ic = uvs.length; i < ic; i += 2) {
    uvs[i] = uvs[i] + offsetX;
    uvs[i + 1] = uvs[i + 1] + offsetY;
  }
}

export function polyLine3ToCylinder(line: PolyLine3, cylinderRadius: number): CylinderGeometry {
  const start = new Vector3(line[0], line[1], line[2]);
  const end = new Vector3(line[3], line[4], line[5]);
  const distance = start.distanceTo(end);

  const cylinder = new CylinderGeometry(cylinderRadius, cylinderRadius, distance, 10, 10, false);

  const orientation = new Matrix4();
  const offsetRotation = new Matrix4();
  const offsetPosition = new Matrix4();

  offsetPosition.makeTranslation(0.5 * (start.x + end.x), 0.5 * (start.y + end.y), 0.5 * (start.z + end.z));
  orientation.multiply(offsetPosition);
  orientation.lookAt(start, end, new Vector3(0, 1, 0));
  offsetRotation.makeRotationX(0.5 * Math.PI);
  orientation.multiply(offsetRotation);
  cylinder.applyMatrix4(orientation);

  return cylinder;
}

export function planarMesh3NormalToCylinder(planarMesh: Mesh, normalLength?: number): CylinderGeometry | undefined {
  const geometry = planarMesh.geometry as BufferGeometry;
  const position: BufferAttribute = geometry.getAttribute("position") as BufferAttribute;
  console.assert(position.isBufferAttribute);
  if (!geometry.index) {
    return undefined;
  }
  const indices = geometry.index.array;

  const vertexCount = position.count;
  const k = 1 / vertexCount;

  let [x, y, z] = [0, 0, 0];
  for (let i = 0; i < vertexCount; i++) {
    x += k * position.getX(i);
    y += k * position.getY(i);
    z += k * position.getZ(i);
  }

  const [nX, nY, nZ] = vec3TriangleNormal(
    position.getX(indices[0]),
    position.getY(indices[0]),
    position.getZ(indices[0]),
    position.getX(indices[1]),
    position.getY(indices[1]),
    position.getZ(indices[1]),
    position.getX(indices[2]),
    position.getY(indices[2]),
    position.getZ(indices[2]),
  );

  const aabb3 = polyLine3AABB3(position.array);
  const l = normalLength ?? aabb3DiagLength(aabb3);
  const r = l / 100;

  const faceNormalLine = polyLine3ByTwoPoints(x, y, z, x + l * nX, y + l * nY, z + l * nZ);
  return polyLine3ToCylinder(faceNormalLine, r);
}

/**
 * Returns an RGB color stored in an array of unsigned bytes as a CSS hex string, as in: #ff0000.
 * Color Should be in SRGB color space, no color space conversion is performed here.
 */
export function colorUnsignedByteArrayToCSSHexString(color: number[] | Uint8Array | Uint8ClampedArray) {
  const value = ((color[0] & 255) << 16) | ((color[1] & 255) << 8) | (color[2] & 255);
  let str = value.toString(16);
  console.assert(str.length <= 6);
  while (str.length < 6) {
    str = "0" + str;
  }
  return "#" + str;
}

export function mat3ToTHREE(mat: Mat3): THREE.Matrix3 {
  const threeMat = new THREE.Matrix3();
  // Internally three.js stores the matrix as column-major like Mat3 does, but set takes arguments in row-major order.
  threeMat.set(mat[0], mat[3], mat[6], mat[1], mat[4], mat[7], mat[2], mat[5], mat[8]);
  return threeMat;
}

export function mat3ToTHREETransformMat4(mat: Mat3, dst: THREE.Matrix4 = new THREE.Matrix4()): THREE.Matrix4 {
  // Keep the affine transform matrix x and y translate values in the last row / column.
  dst.set(mat[0], mat[3], 0, mat[6], mat[1], mat[4], 0, mat[7], 0, 0, 1, 0, mat[2], mat[5], 0, mat[8]);
  return dst;
}

export function mat4ToTHREE(mat: Mat4): THREE.Matrix4 {
  const threeMat = new THREE.Matrix4();
  // Internally three.js stores the matrix as column-major like Mat4 does, but set takes arguments in row-major order.
  threeMat.set(
    mat[0],
    mat[4],
    mat[8],
    mat[12],
    mat[1],
    mat[5],
    mat[9],
    mat[13],
    mat[2],
    mat[6],
    mat[10],
    mat[14],
    mat[3],
    mat[7],
    mat[11],
    mat[15],
  );
  return threeMat;
}

export function lightIntensityToThreePhysicalLight(intensity: number) {
  return intensity * Math.PI;
}
